import { useCallback, useEffect, useRef, useState } from 'react'
import useSearchFilters from './hooks/use-search-filters'
import { useQuery } from '@tanstack/react-query'
import {
  getSearchedVehiclesInfo,
  getSearchedVehiclesInfoKey,
  searchVehicles,
  searchVehiclesKey,
} from '../../api-client/search'

export default function useViewModel() {
  const [{ query, elasticQuery, isQueryValid }, querySetters] = useSearchFilters()
  const {
    location: { zipCode, latitude, longitude },
    pageIndex,
  } = query
  const { setPageIndex, setListingVehicles, setListingAdditionalInfo } = querySetters

  const [isZipCodeModalOpen, setIsZipCodeModalOpen] = useState(false)
  const [searchedVehiclesIds, setSearchedVehiclesIds] = useState([])
  const [lastPageFetched, setLastPageFetched] = useState(false)

  useEffect(() => {
    if (!zipCode || latitude == null || longitude == null) {
      setIsZipCodeModalOpen(true)
    }
  }, [latitude, longitude, zipCode])

  const mouseDownTarget = useRef()
  const searchScroll = useRef(null)
  const lastVehicleRef = useRef()

  const {
    data: vehiclesData,
    isFetching,
  } = useQuery({
    queryKey: [searchVehiclesKey, elasticQuery],
    queryFn: () => searchVehicles(elasticQuery),
    enabled: isQueryValid,
  })

  useEffect(() => {
    if (!vehiclesData) return

    const vehicleIds = vehiclesData.vehicles?.map((vehicle) => vehicle.id) ?? []
    setSearchedVehiclesIds(vehicleIds)
    setListingVehicles(vehiclesData)
    setLastPageFetched(vehiclesData.isLastPage)
  }, [vehiclesData, setListingVehicles])

  const {
    data: vehiclesInfoData,
    isFetching: isVehiclesInfoFetching,
  } = useQuery({
    queryKey: [getSearchedVehiclesInfoKey, searchedVehiclesIds],
    queryFn: () => getSearchedVehiclesInfo(searchedVehiclesIds),
    enabled: searchedVehiclesIds.length > 0,
  })

  useEffect(() => {
    if (!vehiclesInfoData) return
    setListingAdditionalInfo(vehiclesInfoData)
  }, [vehiclesInfoData, setListingAdditionalInfo])

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0]

      if (target.isIntersecting && !lastPageFetched && !isFetching && !isVehiclesInfoFetching) {
        setPageIndex(pageIndex + 1)
      }
    },
    [lastPageFetched, isFetching, isVehiclesInfoFetching, setPageIndex, pageIndex],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '10px',
      threshold: 0,
    })

    const element = lastVehicleRef.current
    if (element) observer.observe(element)

    return () => {
      if (element) observer.unobserve(element)
    }
  }, [handleObserver])

  return {
    query,
    querySetters,
    isFetching: isFetching || isVehiclesInfoFetching,
    mouseDownTarget,
    searchScroll,
    lastVehicleRef,
    isZipCodeModalOpen,
    setIsZipCodeModalOpen,
  }
}